import React, { useState, useEffect, useCallback } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useOnboardingWalkthrough } from '../../../../context/OnboardingWalkthroughContext';
import CarouselCard from './CarouselCard';
import PaginationDots from './PaginationDots';
import { highlightMobileTab, removeAllHighlights } from '../../../../utils/walkthroughTabHighlighting';

/**
 * MobileWalkthroughCarousel Component
 * Mobile-optimized walkthrough with swipeable cards
 */
const MobileWalkthroughCarousel = () => {
  const {
    isActive,
    currentStep,
    currentStepIndex,
    totalSteps,
    handleNext,
    handlePrevious,
    handleSkip
  } = useOnboardingWalkthrough();

  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // Handle tab highlighting
  useEffect(() => {
    if (!isActive || !currentStep) {
      removeAllHighlights();
      return;
    }

    if (currentStep.highlightTab) {
      highlightMobileTab(currentStep.highlightTab);
    }

    return () => removeAllHighlights();
  }, [isActive, currentStep]);

  // Swipe handlers
  const handleTouchStart = useCallback((e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  }, []);

  const handleTouchMove = useCallback((e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && currentStepIndex < totalSteps - 1) {
      handleNext();
    }
    if (isRightSwipe && currentStepIndex > 0) {
      handlePrevious();
    }
  }, [touchStart, touchEnd, currentStepIndex, totalSteps, handleNext, handlePrevious]);

  // Click/tap handlers for left and right navigation zones
  const handleContainerClick = useCallback((e) => {
    // Ignore clicks on buttons and interactive elements
    if (e.target.closest('button') || e.target.closest('a')) {
      return;
    }

    const containerWidth = e.currentTarget.offsetWidth;
    const clickX = e.clientX;
    const clickPosition = clickX / containerWidth;

    // Left third of screen goes back, right third goes forward
    if (clickPosition < 0.33 && currentStepIndex > 0) {
      handlePrevious();
    } else if (clickPosition > 0.67 && currentStepIndex < totalSteps - 1) {
      handleNext();
    }
  }, [currentStepIndex, totalSteps, handleNext, handlePrevious]);

  const isLastStep = currentStepIndex === totalSteps - 1;

  if (!isActive || !currentStep) {
    return null;
  }

  return (
    <div className="mobile-walkthrough-carousel">
      {/* Backdrop */}
      <div className="carousel-backdrop" aria-hidden="true" />

      {/* Carousel Container */}
      <div
        className="carousel-container"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={handleContainerClick}
        role="dialog"
        aria-modal="true"
        aria-labelledby="carousel-title"
      >
        {/* Close Button */}
        <button
          onClick={handleSkip}
          className="carousel-close-button"
          aria-label="Close walkthrough"
        >
          <XMarkIcon className="h-6 w-6" />
        </button>

        <div className={`carousel-card-wrapper ${currentStep.isWelcome ? 'carousel-card-welcome' : 'carousel-card-transparent'}`}>
          <CarouselCard step={currentStep} isWelcome={currentStep.isWelcome} />

          <div className="carousel-footer">
            <PaginationDots total={totalSteps} current={currentStepIndex} />

            {/* Swipe hint or finish button */}
            <div className="carousel-actions">
              {!isLastStep ? (
                <div className="swipe-hint-container">
                  <p className="swipe-hint">Swipe to continue</p>
                </div>
              ) : (
                <button onClick={handleNext} className="carousel-finish-button">
                  Get Started
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileWalkthroughCarousel;
